import {
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import {
    allowDecimalNumbers,
    preventSpecialCharacters
} from "../../../../utils/utilityFunctions";
import dayjs from "dayjs";
import { CurrencyConversionToWords } from "../commonComponents";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import * as yup from 'yup';
import { numberRegex } from "../../../../utils/regex";
import {
    GetCounterPartyListResponseBody, GetNonMfBrokersListResponseBody,
    GetTradeCalculationResponseBody
} from "../../../../redux-store/types/orderManagement";
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useRef
} from "react";
import _ from 'lodash';
import { getTradeCalculation } from "../../../../redux-store/actions/orderManagement";
import { showError } from "../../../../redux-store/actions/auth";

export default function BondsForm({
    securitiesData,
    fetchSecuritiesData,
    setValidations,
    nonMfBrokersList,
    fetchNonMfBrokers,
    counterPartyList,
    fetchCounterPartyList,
    setLoadingStatus,
    isLoading
}: {
    securitiesData: Record<string, any>;
    fetchSecuritiesData: (searchInput: string) => void;
    setValidations: (values: Record<string, any>) => void;
    nonMfBrokersList: GetNonMfBrokersListResponseBody;
    fetchNonMfBrokers: (searchInput: string) => void;
    counterPartyList: GetCounterPartyListResponseBody;
    fetchCounterPartyList: (searchInput: string) => void;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>
    isLoading: boolean;
}): JSX.Element {
    const dispatch = useDispatch();

    const { currencySymbol, locale = "en-IN" } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        setTouched,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleOrderDateChange = (selectedDate: any) => {
        setFieldValue('orderDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleCurrencyFieldPaste = (event: React.ClipboardEvent<HTMLInputElement>, fieldName: string) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = `${clipboardData ?? ""}`?.replaceAll(",", "")

        setFieldValue(fieldName, modifiedText);
    }

    const handleSecurityChange = (selectedObj: Record<string, any>) => {
        const refactoredQtySaleable = (!Number.isNaN(Number(selectedObj?.qtySaleable ?? 0)) ? Number(selectedObj?.qtySaleable ?? 0) : 0)

        if (values?.transactionType === "Sell") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup
                    .string()
                    .required("Quantity is required")
                    .test('is-number', 'Quantity should be a valid number', function (value) { return !Number.isNaN(Number(value)) })
                    .test('whole-number', 'Quantity should be a valid number', function (value) {
                        return numberRegex.test(`${value ?? ""}`);
                    })
                    .test('transaction-type-sell', `Quantity should be less than or equal to ${refactoredQtySaleable}`, function (value) {
                        return Number(value) <= refactoredQtySaleable
                    })
                ,
            }))
        } else if (values?.transactionType !== "Sell") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup
                    .string()
                    .required("Quantity is required")
                    .test('is-number', 'Quantity should be a valid number', function (value) { return !Number.isNaN(Number(value)) })
                    .test('whole-number', 'Quantity should be a valid number', function (value) {
                        return numberRegex.test(`${value ?? ""}`);
                    })
            }))
        }

        resetForm({
            values: {
                ...values,
                security: selectedObj,
                assetClass: selectedObj?.assetName ?? "",
                counterParty: null,
                broker: null,
                issuer: selectedObj?.issuerName ?? "",
                quantity: "",
                // cleanPrice: (!Number.isNaN(Number(selectedObj?.marketPrice ?? 0)) ? Number(selectedObj?.marketPrice ?? 0) : 0)?.toLocaleString(locale),
                cleanPrice: "",
                acquisitionCost: "",
                stampDuty: "",
                accruedInterest: "",
                settlementAmount: "",
                tradeCalculationData: {},
                settlementBankNumber: selectedObj
                    ? {
                        bankCode: selectedObj?.bankCode ?? "",
                        bankAccCcy: selectedObj?.bankAccCcy ?? "",
                        bankAccType: selectedObj?.bankAccType ?? "",
                        bankAccNo: selectedObj?.bankAccNo ?? "",
                        bankAccCode: selectedObj?.bankAccCode ?? "",
                        bankName: selectedObj?.bankName ?? "",
                        label: selectedObj?.bankAccNo ?? "",
                        value: selectedObj?.bankAccNo ?? ""
                    }
                    : null,
                settlementBank: selectedObj?.bankName ?? "",
            }
        })
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {

        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                security: null,
                assetClass: "",
                counterParty: null,
                broker: null,
                issuer: "",
                quantity: "",
                cleanPrice: "",
                acquisitionCost: "",
                stampDuty: "",
                accruedInterest: "",
                settlementAmount: "",
                tradeCalculationData: {},
                settlementBankNumber: null,
                settlementBank: "",
                settlementMethod: ""
            }
        })
    }

    const handleOnBlurCleanPrice = async (e: React.ChangeEvent<any>) => {
        try {
            setTouched({ cleanPrice: true }, true)

            const { target: fieldTarget } = e;
            const { value: enteredValue } = fieldTarget;

            const modifiedEnteredValue = enteredValue?.trim() ?? ""

            const formattedModifiedEnteredValue = Number(modifiedEnteredValue)?.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4
            })
            const formattedStampDuty = `${values?.stampDuty ?? ""}`?.replaceAll(',', '')

            if (
                Number.isNaN(Number(modifiedEnteredValue)) ||
                Number.isNaN(Number(formattedStampDuty)) ||
                Number.isNaN(Number(values?.quantity ?? "")) ||
                `${modifiedEnteredValue ?? ""}`.length === 0 ||
                Number(modifiedEnteredValue) === 0 ||
                Number(values?.quantity ?? "") === 0
            ) {
                return
            }

            setLoadingStatus("apiLoading")

            const payload = {
                // TEMP CHANGES
                // portfolioId: "TW100026",
                // security: "887",
                // quantity: Number(enteredValue ?? ""),
                // price: 10, //Clean Price
                // transactionType: "Buy"
                // TEMP CHANGES

                portfolioId: values?.account ?? "",
                security: values?.security?.security ?? "",
                quantity: Number(values?.quantity ?? ""),
                price: Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', '')), //Clean Price
                transactionType: values?.transactionType ?? ""
            }
            const response = await (dispatch(getTradeCalculation(payload)) as unknown as Promise<GetTradeCalculationResponseBody>);

            if (!response?.isSuccess) {
                dispatch(showError(response?.message ?? "No Data Available"))
            }

            if (response?.isSuccess) {


                let existingSettlementAmount = (!Number.isNaN(Number(response?.tradeCalculationData?.netAmountTcy ?? 0)) ? Number(response?.tradeCalculationData?.netAmountTcy ?? 0) : 0)

                // Formula =>
                // Transaction Type => Buy => Settlement Amount = Settlement Amount + Stamp Duty
                // Transaction Type => Sell => Settlement Amount = Settlement Amount - Stamp Duty

                if (values?.transactionType === "Buy") {
                    existingSettlementAmount = existingSettlementAmount + Number(formattedStampDuty)
                } else if (values?.transactionType === "Sell") {
                    existingSettlementAmount = existingSettlementAmount - Number(formattedStampDuty)
                }

                resetForm({
                    values: {
                        ...values,
                        cleanPrice: formattedModifiedEnteredValue,
                        tradeCalculationData: response?.tradeCalculationData ?? {},
                        // Acquisition Cost
                        acquisitionCost: (!Number.isNaN(Number(response?.tradeCalculationData?.acquCostTcy ?? 0)) ? Number(response?.tradeCalculationData?.acquCostTcy ?? 0) : 0)?.toLocaleString(locale),
                        // Accrued Interest
                        accruedInterest: (!Number.isNaN(Number(response?.tradeCalculationData?.grossIntTcy ?? 0)) ? Number(response?.tradeCalculationData?.grossIntTcy ?? 0) : 0)?.toLocaleString(locale),
                        // Settlement Amount
                        settlementAmount: Number(existingSettlementAmount)?.toLocaleString(locale),
                    }
                })
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
        }
    }

    const handleOnBlurQuantity = async (e: React.ChangeEvent<any>) => {
        try {
            setTouched({ quantity: true }, true)

            const { target: fieldTarget } = e;
            const { value: enteredValue } = fieldTarget;

            const modifiedEnteredValue = enteredValue?.trim() ?? ""

            const formattedCleanPrice = Number(`${values?.cleanPrice ?? ""}`?.replaceAll(',', ''))
            const formattedStampDuty = `${values?.stampDuty ?? ""}`?.replaceAll(',', '')

            if (
                `${modifiedEnteredValue ?? ""}`.length === 0 ||
                formattedCleanPrice === 0 ||
                Number.isNaN(Number(formattedStampDuty)) ||
                Number.isNaN(Number(formattedCleanPrice)) ||
                Number.isNaN(Number(modifiedEnteredValue ?? ""))
            ) {
                return
            }

            setLoadingStatus("apiLoading")

            const payload = {
                // TEMP CHANGES
                // portfolioId: "TW100026",
                // security: "887",
                // quantity: Number(enteredValue ?? ""),
                // price: 10, //Clean Price
                // transactionType: "Buy"
                // TEMP CHANGES

                portfolioId: values?.account ?? "",
                security: values?.security?.security ?? "",
                quantity: Number(modifiedEnteredValue ?? ""),
                price: formattedCleanPrice, //Clean Price
                transactionType: values?.transactionType ?? ""
            }
            const response = await (dispatch(getTradeCalculation(payload)) as unknown as Promise<GetTradeCalculationResponseBody>);

            if (!response?.isSuccess) {
                dispatch(showError(response?.message ?? "No Data Available"))
            }

            if (response?.isSuccess) {

                let existingSettlementAmount = (!Number.isNaN(Number(response?.tradeCalculationData?.netAmountTcy ?? 0)) ? Number(response?.tradeCalculationData?.netAmountTcy ?? 0) : 0)

                // Formula =>
                // Transaction Type => Buy => Settlement Amount = Settlement Amount + Stamp Duty
                // Transaction Type => Sell => Settlement Amount = Settlement Amount - Stamp Duty

                if (values?.transactionType === "Buy") {
                    existingSettlementAmount = existingSettlementAmount + Number(formattedStampDuty)
                } else if (values?.transactionType === "Sell") {
                    existingSettlementAmount = existingSettlementAmount - Number(formattedStampDuty)
                }

                resetForm({
                    values: {
                        ...values,
                        quantity: modifiedEnteredValue,
                        tradeCalculationData: response?.tradeCalculationData ?? {},
                        // Acquisition Cost
                        acquisitionCost: (!Number.isNaN(Number(response?.tradeCalculationData?.acquCostTcy ?? 0)) ? Number(response?.tradeCalculationData?.acquCostTcy ?? 0) : 0)?.toLocaleString(locale),
                        // Accrued Interest
                        accruedInterest: (!Number.isNaN(Number(response?.tradeCalculationData?.grossIntTcy ?? 0)) ? Number(response?.tradeCalculationData?.grossIntTcy ?? 0) : 0)?.toLocaleString(locale),
                        // Settlement Amount
                        settlementAmount: Number(existingSettlementAmount)?.toLocaleString(locale),
                    }
                })
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
        }
    }

    const handleOnBlurStampDuty = async (e: React.ChangeEvent<any>) => {
        try {
            setTouched({ stampDuty: true }, true)

            const { target: fieldTarget } = e;
            const { value: enteredValue } = fieldTarget;

            const modifiedEnteredValue = enteredValue?.trim() ?? ""

            const formattedModifiedEnteredValue = Number(modifiedEnteredValue)?.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })
            const formattedCleanPrice = Number(`${values?.cleanPrice ?? ""}`?.replaceAll(',', ''))

            if (
                Number.isNaN(Number(modifiedEnteredValue)) ||
                Number.isNaN(Number(formattedCleanPrice)) ||
                Number.isNaN(Number(values?.quantity ?? "")) ||
                formattedCleanPrice === 0 ||
                `${values?.quantity ?? ""}`?.length === 0
            ) {
                return
            }

            setLoadingStatus("apiLoading")

            const payload = {
                // TEMP CHANGES
                // portfolioId: "TW100026",
                // security: "887",
                // quantity: Number(enteredValue ?? ""),
                // price: 10, //Clean Price
                // transactionType: "Buy"
                // TEMP CHANGES

                portfolioId: values?.account ?? "",
                security: values?.security?.security ?? "",
                quantity: Number(values?.quantity ?? ""),
                price: formattedCleanPrice,
                transactionType: values?.transactionType ?? ""
            }
            const response = await (dispatch(getTradeCalculation(payload)) as unknown as Promise<GetTradeCalculationResponseBody>);

            if (!response?.isSuccess) {
                dispatch(showError(response?.message ?? "No Data Available"))
            }

            if (response?.isSuccess) {

                let existingSettlementAmount = (!Number.isNaN(Number(response?.tradeCalculationData?.netAmountTcy ?? 0)) ? Number(response?.tradeCalculationData?.netAmountTcy ?? 0) : 0)

                // Formula =>
                // Transaction Type => Buy => Settlement Amount = Settlement Amount + Stamp Duty
                // Transaction Type => Sell => Settlement Amount = Settlement Amount - Stamp Duty

                if (values?.transactionType === "Buy") {
                    existingSettlementAmount = existingSettlementAmount + (!Number.isNaN(Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', ''))) ? Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', '')) : 0)
                } else if (values?.transactionType === "Sell") {
                    existingSettlementAmount = existingSettlementAmount - (!Number.isNaN(Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', ''))) ? Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', '')) : 0)
                }

                resetForm({
                    values: {
                        ...values,
                        stampDuty: `${modifiedEnteredValue ?? ""}`.length !== 0 ? formattedModifiedEnteredValue : "",
                        tradeCalculationData: response?.tradeCalculationData ?? {},
                        // Acquisition Cost
                        acquisitionCost: (!Number.isNaN(Number(response?.tradeCalculationData?.acquCostTcy ?? 0)) ? Number(response?.tradeCalculationData?.acquCostTcy ?? 0) : 0)?.toLocaleString(locale),
                        // Accrued Interest
                        accruedInterest: (!Number.isNaN(Number(response?.tradeCalculationData?.grossIntTcy ?? 0)) ? Number(response?.tradeCalculationData?.grossIntTcy ?? 0) : 0)?.toLocaleString(locale),
                        // Settlement Amount
                        settlementAmount: Number(existingSettlementAmount)?.toLocaleString(locale),
                    }
                })

                setTouched({ settlementAmount: true }, true)
            }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
        }
    }

    const refactoredCleanPrice = `${values?.cleanPrice ?? ""}`?.replaceAll(',', '')
    const refactoredAcquisitionCost = `${values?.acquisitionCost ?? ""}`?.replaceAll(',', '')
    const refactoredStampDuty = `${values?.stampDuty ?? ""}`?.replaceAll(',', '')
    const refactoredAccruedInterest = `${values?.accruedInterest ?? ""}`?.replaceAll(',', '')
    const refactoredSettlementAmount = `${values?.settlementAmount ?? ""}`?.replaceAll(',', '')

    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={handleOrderDateChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Buy" },
                        { name: "Sell" }, //call holding api
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleTransactionTypeChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="security"
                    label="Security *"
                    options={securitiesData?.securities?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: obj?.securityName ?? "",
                        value: obj?.security ?? ""
                    })) ?? []}
                    fetchData={fetchSecuritiesData}
                    disabled={isLoading || !values?.transactionType}
                    onChange={handleSecurityChange}
                    filterOptions={(x) => x}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="assetClass"
                    label="Asset Class"
                    placeholder=""
                    disabled={true}
                    value={values?.assetClass ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="counterParty"
                    label="Counter Party *"
                    options={counterPartyList?.counterPartyList?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: obj?.description ?? "",
                        value: obj?.identifier ?? ""
                    })) ?? []}
                    fetchData={fetchCounterPartyList}
                    disabled={isLoading || !values?.security}
                    filterOptions={(x) => x}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="broker"
                    label="Broker *"
                    options={nonMfBrokersList?.brokers?.map(obj => ({
                        ...obj,
                        label: obj?.description ?? "",
                        value: obj?.identifier ?? ""
                    })) ?? []}
                    fetchData={fetchNonMfBrokers}
                    filterOptions={(x) => x}
                    disabled={isLoading || !values?.security}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="issuer"
                    label="Issuer"
                    placeholder=""
                    disabled={true}
                    value={values?.issuer ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4} sx={{ position: "relative" }}>
                {values?.transactionType === "Sell" && (
                    <Typography
                        variant="overline_400"
                        sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "0px"
                        }}
                    >
                        Available Quantity: {!Number.isNaN(Number(values?.security?.qtySaleable ?? 0)) ? Number(values?.security?.qtySaleable ?? 0) : 0}
                    </Typography>
                )}
                <MFTextField
                    name="quantity"
                    label="Quantity *"
                    placeholder=""
                    disabled={isLoading || !values?.security}
                    onKeyDown={preventSpecialCharacters}
                    value={values?.quantity ?? ""}
                    onBlur={handleOnBlurQuantity}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="cleanPrice"
                    label="Clean Price *"
                    placeholder=""
                    startAdornment={values?.cleanPrice ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.quantity) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={isLoading || !values?.quantity}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "cleanPrice")}
                    value={values?.cleanPrice ?? ""}
                    onBlur={handleOnBlurCleanPrice}
                />
                <CurrencyConversionToWords value={refactoredCleanPrice} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="acquisitionCost"
                    label="Acquisition Cost"
                    placeholder=""
                    startAdornment={values?.acquisitionCost ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    // disabled={!values?.security}
                    disabled={true}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "acquisitionCost")}
                    value={values?.acquisitionCost ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAcquisitionCost} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="stampDuty"
                    label="Stamp Duty"
                    placeholder=""
                    startAdornment={values?.stampDuty ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={isLoading || !values?.security}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "stampDuty")}
                    value={values?.stampDuty ?? ""}
                    onBlur={handleOnBlurStampDuty}
                />
                <CurrencyConversionToWords value={refactoredStampDuty} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="accruedInterest"
                    label="Accrued Interest"
                    placeholder=""
                    startAdornment={values?.accruedInterest ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    // disabled={!values?.security}
                    disabled={true}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "accruedInterest")}
                    value={values?.accruedInterest ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAccruedInterest} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="settlementAmount"
                    label="Settlement Amount"
                    placeholder=""
                    startAdornment={values?.settlementAmount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    // disabled={!values?.security}
                    disabled={true}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "settlementAmount")}
                    value={values?.settlementAmount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredSettlementAmount} />
            </Grid>
            <Grid item xs={12}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid>
    )
}